/// <amd-module name="VSS/Features/Frame/ContributedMenuItemProvider" />
import React = require("react");
import { IReadonlyObservableArray, ObservableLike } from "VSS/Core/Observable";
import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import { IContextualMenuProps } from "OfficeFabric/ContextualMenu";
import { IVssPageContext } from "VSS/Platform/Context";
import { WrappedComponent } from "VSS/Platform/Layout";
import { contributionIconToIconProps } from "./ContributedIconUtil";
import { IContributedCommand, IVssContributionService, IContributedService } from "VSS/Platform/Contribution";
import {
    IVssContextualMenuProps,
    IVssContextualMenuItemProvider,
    IVssContextualMenuItem
} from "VSSUI/Components/VssContextualMenu/VssContextualMenu.Props";
import { IVssContributableMenuService } from "./IVssContributableMenuService";
import { IVssContributableActionService } from "IVssContributableActionService";
import { IContributedMenuItem } from "./IContributedMenuItem";
import { IContributedIconDefinition } from "VSS/Platform/ContributionTypes";
import { IIconProps } from "VSSUI/Icon";

interface IVssServerMenuItem extends IVssContextualMenuItem {
    command?: string;
}

interface IMenuItemContributionData {
    [menuId: string]: IVssServerMenuItem[];
}

interface IVssContextualMenuItemWithCommand extends IVssContextualMenuItem {
    command?: IContributedCommand;
    menuId?: string;
}

interface IVssContextualMenuItemProviderWithMenuId extends IVssContextualMenuItemProvider {
    menuId?: string;
}

interface IContributedAction {
    text: string;
    title?: string;
    command?: IContributedCommand;
    iconProps?: IIconProps;
    icon?: string | IContributedIconDefinition;
    important?: boolean;
    registeredObjectId?: string;
    uri?: string;
    groupId?: string;
}

/**
 *
 */
export class ContributedMenuItemProvider<T> implements IVssContextualMenuItemProvider {
    private _menuId: string;
    private _pageContext: IVssPageContext;
    private _data: T | undefined;

    constructor(pageContext: IVssPageContext, menuId: string, data?: T) {
        this._pageContext = pageContext;
        this._menuId = menuId;
        this._data = data;
    }

    public loadItems(itemsUpdated: (menuItems: IVssContextualMenuItem[], itemsRemoved?: string[]) => void, itemsInitialized: () => void): void {
        const menuItems: IVssContextualMenuItem[] = [];
        const menuItemsLookup: { [menuItemId: string]: string } = {};
        const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");

        // Determine whether or not there are any menu-items contributed through shared data.
        const menuData: IMenuItemContributionData | undefined = contributionService.getSharedData<IMenuItemContributionData>("_menuItems");

        // If we have shared menu data, then look up items for the targeted menu
        if (menuData) {
            const sharedMenuItems = menuData[this._menuId];

            // If menu items exist for the targeted menu, then update the menu.
            if (sharedMenuItems && sharedMenuItems.length > 0) {
                sharedMenuItems.forEach(sharedMenuItem => {
                    sharedMenuItem.onClick = sharedMenuItem.command ? this.onMenuItem : undefined;
                    menuItems.push(sharedMenuItem);
                });

                itemsUpdated(this.prepareMenuItems(menuItems));
            }
        }

        // Add the menu items that are statically defined as child menu-item contributions.
        const children = contributionService.getContributionChildren(this._menuId, "ms.vss-web.menu-item");
        if (children) {
            for (let i = 0; i < children.length; i++) {
                const childId = children[i];

                // look up the contribution for the child
                let menuContribution = contributionService.getContribution<IVssContextualMenuItemWithCommand>(childId);

                if (menuContribution) {
                    menuItems.push(this.createMenuItem(menuContribution, childId));
                    menuItemsLookup[childId] = childId;
                }
            }
        }

        const actionChildren = contributionService.getContributionChildren(this._menuId, "ms.vss-web.action");
        if (actionChildren) {
            for (let i = 0; i < actionChildren.length; i++) {
                const childId = actionChildren[i];

                // look up the contribution for the child
                const actionContribution = contributionService.getContribution<IContributedAction>(childId);

                if (actionContribution) {
                    menuItems.push(this.createMenuItemFromAction(actionContribution, childId));
                    menuItemsLookup[childId] = childId;
                }
            }
        }

        // In addition to menu items that directly target a menu contribution,
        // we want to find all menu items with a menuId property of this menu,
        // regardless of how they got into the contribution tree.
        const allMenuItems = contributionService.getContributionsByType<IVssContextualMenuItemWithCommand>("ms.vss-web.menu-item");
        if (allMenuItems) {
            for (let menuItemId in allMenuItems) {
                if (!menuItemsLookup[menuItemId]) {
                    const menuItem = allMenuItems[menuItemId];

                    if (menuItem.menuId === this._menuId) {
                        menuItems.push(this.createMenuItem(menuItem, menuItemId));
                        menuItemsLookup[menuItemId] = menuItemId;
                    }
                }
            }
        }

        if (menuItems.length > 0) {
            itemsUpdated(this.prepareMenuItems(menuItems));
        }

        // Go through each of the child providers adding the menu items that are resolved.
        const providerIds = contributionService.getContributionChildren(this._menuId, "ms.vss-web.menu-provider") || [];
        const providerIdsLookup: { [providerId: string]: string } = {};
        for (let i = 0; i < providerIds.length; i++) {
            providerIdsLookup[providerIds[i]] = providerIds[i];
        }

        // In addition to menu item providers that directly target a menu contribution,
        // we want to find all menu item providers with a menuId property of this menu,
        // regardless of how they got into the contribution tree.
        const allMenuProviders = contributionService.getContributionsByType<IVssContextualMenuItemProviderWithMenuId>("ms.vss-web.menu-provider");
        if (allMenuProviders) {
            for (let menuProviderItem in allMenuProviders) {
                if (!providerIdsLookup[menuProviderItem]) {
                    const menuProvider = allMenuProviders[menuProviderItem];

                    if (menuProvider.menuId === this._menuId) {
                        providerIds.push(menuProviderItem);
                        providerIdsLookup[menuProviderItem] = menuProviderItem;
                    }
                }
            }
        }

        if (providerIds && providerIds.length > 0) {
            // Keep track of menu providers that need to initialize
            const uninitializedProviders = providerIds.slice(0);

            for (let childId of providerIds) {
                contributionService
                    .getService<IVssContributableMenuService>(childId, true)
                    .then((menuContributionService: IVssContributableMenuService | undefined) => {
                        if (menuContributionService) {
                            const onItemsUpdated = (menuItems: IVssContextualMenuItem[], removedItems?: string[]): void => {
                                // Update the set of menu items
                                itemsUpdated(this.prepareMenuItems(menuItems), removedItems);

                                // Determine if all menu providers have now initialized.
                                // If so, let the parent know this provider is not initialized.
                                const indexOfProvider = uninitializedProviders.indexOf(childId);
                                if (indexOfProvider > -1) {
                                    uninitializedProviders.splice(indexOfProvider, 1);
                                    if (uninitializedProviders.length === 0) {
                                        itemsInitialized();
                                    }
                                }
                            };
                            if (typeof menuContributionService.loadItems === "function") {
                                menuContributionService.loadItems(onItemsUpdated);
                            } else if (typeof menuContributionService.getMenuItems === "function") {
                                // Convert contributed menu items to contenxtual menu items
                                const processItems = (items: IContributedMenuItem[] | IReadonlyObservableArray<IContributedMenuItem>) => {
                                    const processedItems: IVssContextualMenuItem[] = [];
                                    for (let i = 0; i < items.length; i++) {
                                        let contributedMenuItem: IContributedMenuItem;
                                        contributedMenuItem = ObservableLike.getValue(items)[i];
                                        const menuItem: IVssContextualMenuItem = {
                                            key: childId + i.toString(),
                                            name: contributedMenuItem.text,
                                            groupId: contributedMenuItem.groupId,
                                            iconProps: contributionIconToIconProps(
                                                this._pageContext,
                                                contributedMenuItem.id!,
                                                contributedMenuItem.icon
                                            ),
                                            onClick: (
                                                ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
                                                item?: IContextualMenuItem
                                            ): void => {
                                                if (contributedMenuItem.action) {
                                                    contributedMenuItem.action(this._data);
                                                }
                                            }
                                        };

                                        processedItems.push(menuItem);
                                    }

                                    return processedItems;
                                };

                                const items = menuContributionService.getMenuItems(this._data || {});
                                if (items instanceof Promise) {
                                    items.then((menuItems: IContributedMenuItem[]) => {
                                        onItemsUpdated(processItems(menuItems), []);
                                    });
                                } else {
                                    onItemsUpdated(processItems(items), []);
                                }
                            }
                        }
                    });
            }
        } else {
            // If no menu providers exist, then all items are loaded.
            itemsInitialized();
        }
    }

    private createMenuItem(menuContribution: IVssContextualMenuItemWithCommand, menuItemId: string): IVssContextualMenuItem {
        return {
            command: menuContribution.command,
            href: menuContribution.href,
            iconProps: menuContribution.iconProps || contributionIconToIconProps(this._pageContext, menuContribution.id, menuContribution.icon),
            key: menuItemId,
            name: menuContribution.name,
            rank: menuContribution.rank,
            onClick: menuContribution.command ? this.onMenuItem : undefined,
            groupKey: menuContribution.groupKey
        };
    }

    private createMenuItemFromAction(actionContribution: IContributedAction, actionId: string): IVssContextualMenuItem {
        const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
        return {
            command: actionContribution.command,
            iconProps: actionContribution.iconProps || contributionIconToIconProps(this._pageContext, actionId, actionContribution.icon),
            key: actionId,
            name: actionContribution.text ? actionContribution.text : actionContribution.title,
            groupKey: actionContribution.groupId,
            onClick: (): void => {       
                    if (actionContribution.command) {
                        contributionService.executeCommandEx(actionId, actionContribution.command, this._data);
                    } else {
                        const contributedService: IContributedService = {
                            serviceName: actionContribution.registeredObjectId || actionId,
                            uri: actionContribution.uri,
                            configuration: this._data
                        };

                        contributionService
                            .getServiceEx<IVssContributableActionService>(actionId, contributedService, true)
                            .then(contributedActionService => {
                                if (contributedActionService && contributedActionService.execute) {
                                    contributedActionService.execute(this._data);
                                }
                            });
                    }
            }
        };
    }

    // Ensure the menu items are ready for use, this includes things like owner draw etc.
    private prepareMenuItems(menuItems: IVssContextualMenuItem[]): IVssContextualMenuItem[] {
        for (let menuItem of menuItems) {
            if (menuItem.componentType) {
                menuItem.onRender = this.ownerDrawMenuItem;
            }

            // If the menu item gives a menu id, give its submenu a contributed menu item provider for that id.
            const menuId = (menuItem as IVssContextualMenuItemWithCommand).menuId;
            if (menuId) {
                if (!menuItem.subMenuProps) {
                    menuItem.subMenuProps = {} as IContextualMenuProps;
                }
                (menuItem.subMenuProps as IVssContextualMenuProps).providers = [new ContributedMenuItemProvider(this._pageContext, menuId)];
            }
        }

        return menuItems;
    }

    private ownerDrawMenuItem = (menuItem: IVssContextualMenuItem) => {
        return <WrappedComponent wrappedType={menuItem.componentType!} {...Object.assign({ key: menuItem.key }, menuItem.componentProperties)} />;
    };

    private onMenuItem = (ev?: React.MouseEvent<HTMLElement>, item?: IVssContextualMenuItemWithCommand): void => {
        if (item) {
            const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            if (item.command) {
                contributionService.executeCommandEx(item.key, item.command, this._data);
            } else {
                contributionService.getService<IVssContributableActionService>(item.key, true).then(contributedActionService => {
                    if (contributedActionService && contributedActionService.execute) {
                        contributedActionService.execute(this._data);
                    }
                });
            }
        }
    };
}
